// Dependency-free page metadata (title + description + canonical + robots +
// Open Graph/Twitter). Called from the router on every navigation, and again
// from dynamic pages (product, article) once their data has loaded.
//
// Site-wide defaults come from the admin Meta page (GET /api/meta, applied
// once on boot via applySiteMeta); anything a route or page sets still wins.
// The constants below are the fallback when an override is empty or the API
// is unreachable, and must match index.html.
//
// Note: this runs client-side, so JS-rendering crawlers (Google) see the
// per-route tags, but non-JS social scrapers only see index.html's static
// defaults — admin overrides do not reach them (TD-05 in DESIGN.md). Real
// per-link share previews would need SSR/prerendering.
import type { IconLink, SiteMeta } from '../api/meta';

const BRAND = 'GAMZOR';
const DEFAULT_TITLE = `${BRAND} — Game Keys Marketplace`;
const DEFAULT_DESCRIPTION =
  'GAMZOR — buy game keys instantly at unbeatable prices. Secure checkout, ' +
  'instant delivery, and thousands of PC, Steam, and console titles.';
// Root-relative; resolved to an absolute URL against the current origin.
const DEFAULT_IMAGE = '/og-image.png';

/** The fallbacks above, shown as placeholders in the admin Meta editor. */
export const BUILT_IN_META = {
  title: DEFAULT_TITLE,
  description: DEFAULT_DESCRIPTION,
  siteName: BRAND,
  image: DEFAULT_IMAGE,
  twitterCard: 'summary_large_image',
} as const;

export interface PageMeta {
  /** Page title; the " — <site name>" suffix is appended automatically. */
  title?: string;
  /** Fully-formed title used verbatim (no brand suffix). Overrides `title`. */
  fullTitle?: string;
  description?: string;
  /** Absolute URL or root-relative path for the share image; falls back to the default OG image. */
  image?: string | null;
  /** og:type — defaults to 'website'. Product pages pass 'product', articles 'article'. */
  ogType?: string;
  /** Whether search engines may index this page. Defaults to true. */
  index?: boolean;
  /** Canonical URL — absolute or root-relative; defaults to the current path (query stripped). */
  canonical?: string;
}

function upsertMeta(key: 'name' | 'property', value: string, content: string): void {
  const selector = `meta[${key}="${value}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(key, value);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(key: 'name' | 'property', value: string): void {
  document.head.querySelector(`meta[${key}="${value}"]`)?.remove();
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function toAbsolute(value: string): string {
  if (typeof window === 'undefined') return value;
  return /^https?:\/\//.test(value) ? value : `${window.location.origin}${value}`;
}

/** Collapse whitespace, strip any stray tags, and clamp to a meta-friendly length. */
export function clampDescription(text: string, max = 200): string {
  const clean = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1).trimEnd()}…` : clean;
}

/** Admin overrides from GET /api/meta; null until it has loaded. */
let site: SiteMeta | null = null;
/** Last page call, re-applied when the site overrides arrive after it. */
let lastPage: PageMeta = {};

/** Installs the admin's site-wide overrides and re-applies the current page. */
export function applySiteMeta(next: SiteMeta): void {
  site = next;
  if (next.icons.length) replaceIcons(next.icons);
  setPageMeta(lastPage);
}

/** Swaps index.html's static icon links for the uploaded set. */
function replaceIcons(icons: IconLink[]): void {
  document.head
    .querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
    .forEach((el) => el.remove());
  for (const icon of icons) {
    const el = document.createElement('link');
    el.setAttribute('rel', icon.rel);
    el.setAttribute('sizes', icon.sizes);
    if (icon.type) el.setAttribute('type', icon.type);
    el.setAttribute('href', icon.href);
    document.head.appendChild(el);
  }
}

/**
 * og:image:alt/width/height describe the admin's share image only, so they are
 * emitted while that image is in use and removed when a page brings its own.
 */
function applyShareImageDetails(usesSiteImage: boolean): void {
  const details: Array<['property' | 'name', string, string | undefined]> = [
    ['property', 'og:image:alt', site?.imageAlt],
    ['name', 'twitter:image:alt', site?.imageAlt],
    ['property', 'og:image:width', site?.imageWidth],
    ['property', 'og:image:height', site?.imageHeight],
  ];
  for (const [key, name, value] of details) {
    if (usesSiteImage && value) upsertMeta(key, name, value);
    else removeMeta(key, name);
  }
}

export function setPageMeta(meta: PageMeta = {}): void {
  lastPage = meta;
  const brand = site?.siteName || BRAND;
  const title =
    meta.fullTitle ?? (meta.title ? `${meta.title} — ${brand}` : site?.title || DEFAULT_TITLE);
  const description = (meta.description ?? '').trim() || site?.description || DEFAULT_DESCRIPTION;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  // Canonical drops query/hash so filtered/paginated views collapse to one URL.
  const canonical = meta.canonical ? toAbsolute(meta.canonical) : `${origin}${path}`;
  const indexable = meta.index !== false;
  const usesSiteImage = !meta.image && !!site?.image;
  const image = toAbsolute(meta.image || site?.image || DEFAULT_IMAGE);

  document.title = title;
  upsertMeta('name', 'description', description);
  upsertMeta('name', 'robots', indexable ? 'index, follow' : 'noindex, nofollow');
  upsertLink('canonical', canonical);

  // Open Graph
  upsertMeta('property', 'og:site_name', brand);
  upsertMeta('property', 'og:type', meta.ogType ?? 'website');
  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:image', image);
  applyShareImageDetails(usesSiteImage);

  // Twitter — every page has an image (the default OG artwork is a wide
  // landscape), so the large card is the default unless the admin picked one.
  upsertMeta('name', 'twitter:card', site?.twitterCard || BUILT_IN_META.twitterCard);
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', image);
}
